/**
 * The kitchen game on screen: the buddy card at the top of Discover, the
 * picker, the celebration, and the trophy shelf.
 *
 * Every number comes from the server's summary, which is derived from what
 * actually happened (shared/kitchen.ts). Nothing here awards anything — it
 * only shows, and celebrates the difference between two summaries.
 */
import { useState } from 'react'
import Icon from '../Icon'
import Buddy, { type Mood } from './Buddy'
import { dayLabel } from './Waiting'
import config from '../../../studio42.config.json'
import { BADGES, BUDDIES, LEVELS, XP, hatFor, type BuddyKey, type KitchenSummary, type Level } from '../../../shared/kitchen'
import { nudgeState } from '../../../shared/nudge'
import { chooseBuddy, type BuddyChoice } from '../../lib/kitchen'
import { celebrate, setSound, sound, soundOn } from '../../lib/fun'
import type { RecipeRow } from '../../lib/savenmake'

export const buddyName = (b: BuddyChoice) => b.name || BUDDIES.find((x) => x.key === b.buddy)?.name || 'Buddy'

/** What the buddy says, from what is actually going on in the library. */
function lineFor(recipes: RecipeRow[], summary: KitchenSummary): { text: string; mood: Mood } {
  const now = Date.now()
  const states = recipes.map((r) => ({ r, s: nudgeState(r, config.timezone, now) }))
  const waiting = states.filter((x) => x.s && x.s.kind !== 'planned')
  const next = states
    .filter((x) => x.s?.kind === 'planned')
    .sort((a, b) => (a.r.plannedFor ?? '').localeCompare(b.r.plannedFor ?? ''))[0]

  if (next?.r.plannedFor) return { text: `${next.r.title} ${dayLabel(next.r.plannedFor)}! I'll bring the apron.`, mood: 'wow' }
  if (waiting.length === 1) return { text: `${waiting[0]!.r.title} has been waiting a while. Want to give it a day?`, mood: 'think' }
  if (waiting.length > 1) return { text: `${waiting.length} recipes are waiting on us. Pick one for this week?`, mood: 'think' }
  if (summary.streak.current > 0 && !summary.streak.thisWeek) {
    return { text: `Cook anything this week to keep our ${summary.streak.current}-week streak going.`, mood: 'think' }
  }
  if (summary.facts.saved === 0) return { text: 'Save a recipe you love and we will make it together.', mood: 'happy' }
  if (summary.facts.cooks === 0) return { text: "Let's make our first one. Pick anything that looks good.", mood: 'happy' }
  return { text: 'All caught up. Saved anything tasty lately?', mood: 'happy' }
}

function XpBar({ level, xp }: { level: Level; xp: number }) {
  return (
    <div className="xp">
      <div className="xp-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level.progress * 100)}
        aria-label={level.next ? `${xp} of ${level.next} XP to ${level.nextTitle}` : `${xp} XP, top level`}>
        <div className="xp-fill" style={{ width: `${Math.max(4, level.progress * 100)}%` }} />
      </div>
      <span className="xp-label">
        {level.next ? <><b>{level.next - xp} XP</b> to {level.nextTitle}</> : <b>Top of the kitchen</b>}
      </span>
    </div>
  )
}

/** The card at the top of Discover. Shows the picker until a buddy is chosen. */
export function KitchenHero({ summary, buddy, recipes, onBuddy }: {
  summary: KitchenSummary
  buddy: BuddyChoice | null
  recipes: RecipeRow[]
  onBuddy: (b: BuddyChoice) => void
}) {
  const [picking, setPicking] = useState(false)
  const [muted, setMuted] = useState(!soundOn())

  if (!buddy || picking) {
    return <BuddyPicker current={buddy} onCancel={buddy ? () => setPicking(false) : undefined}
      onChosen={(b) => { setPicking(false); onBuddy(b) }} />
  }

  const { text, mood } = lineFor(recipes, summary)
  const { level, streak } = summary
  return (
    <section className="hero">
      <button type="button" className="hero-buddy" onClick={() => setPicking(true)} aria-label={`Change buddy (${buddyName(buddy)})`}>
        <Buddy kind={buddy.buddy} mood={mood} hat={hatFor(level.index)} size={104} />
      </button>
      <div className="hero-main">
        <div className="hero-top">
          <b className="hero-name">{buddyName(buddy)}</b>
          <span className="level-chip">Lv {level.index + 1} · {level.title}</span>
          <button
            type="button" className="icon-btn" aria-pressed={!muted}
            aria-label={muted ? 'Turn sounds on' : 'Turn sounds off'}
            onClick={() => { setSound(muted); setMuted(!muted); if (muted) sound('tap') }}
          >
            <Icon name={muted ? 'mute' : 'volume'} size={16} />
          </button>
        </div>
        <p className="bubble">{text}</p>
        <XpBar level={level} xp={summary.xp} />
        <p className="streak">
          <Icon name="flame" size={16} />
          {streak.current > 0
            ? <><b>{streak.current}-week streak</b>{streak.thisWeek ? ' · this week counts' : ' · cook this week to keep it'}</>
            : <>Cook this week to start a streak</>}
        </p>
      </div>
    </section>
  )
}

export function BuddyPicker({ current, onChosen, onCancel }: {
  current: BuddyChoice | null
  onChosen: (b: BuddyChoice) => void
  onCancel?: () => void
}) {
  const [pick, setPick] = useState<BuddyKey>(current?.buddy ?? 'tomate')
  const [name, setName] = useState(current?.name ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const chosen = BUDDIES.find((b) => b.key === pick)!

  return (
    <section className="card picker">
      <h2>{current ? 'Switch your kitchen buddy' : 'Pick your kitchen buddy'}</h2>
      <p className="muted small">They cheer you on, keep track of what you have cooked, and nudge you about the recipes you saved.</p>
      <div className="picker-grid" role="radiogroup" aria-label="Buddies">
        {BUDDIES.map((b) => (
          <button
            key={b.key} type="button" role="radio" aria-checked={pick === b.key}
            className={pick === b.key ? 'picker-option is-picked' : 'picker-option'}
            onClick={() => { sound('tap'); setPick(b.key) }}
          >
            <Buddy kind={b.key} mood={pick === b.key ? 'cheer' : 'happy'} size={76} />
            <span>{b.name}</span>
          </button>
        ))}
      </div>
      <p className="bubble">{chosen.blurb}</p>
      <label>
        Give them a name <span className="muted small">— optional</span>
        <input value={name} maxLength={24} placeholder={chosen.name} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="filter-row">
        <button
          type="button" className="primary" style={{ width: 'auto' }} disabled={busy}
          onClick={async () => {
            sound('badge')
            setBusy(true)
            setError(null)
            try {
              const { buddy } = await chooseBuddy(pick, name.trim() || undefined)
              celebrate()
              onChosen(buddy)
            } catch (e) {
              setError((e as Error).message)
            } finally {
              setBusy(false)
            }
          }}
        >
          <Icon name="check" size={16} /> {current ? 'Switch' : "Let's cook"}
        </button>
        {onCancel && <button type="button" className="link" onClick={onCancel}>Cancel</button>}
      </div>
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  )
}

export interface Moment { kind: 'level'; level: Level }
export type AnyMoment = Moment | { kind: 'badge'; key: string }

/** The pop-up for a level or a badge. One at a time; the queue lives in App. */
export function MomentModal({ moment, buddy, onClose }: { moment: AnyMoment; buddy: BuddyChoice | null; onClose: () => void }) {
  const badge = moment.kind === 'badge' ? BADGES.find((b) => b.key === moment.key) : null
  return (
    <div className="moment-backdrop" role="dialog" aria-modal="true" aria-labelledby="moment-title" onClick={onClose}>
      <div className="moment" onClick={(e) => e.stopPropagation()}>
        <p className="moment-kicker">{moment.kind === 'level' ? 'Level up!' : 'Badge unlocked'}</p>
        {buddy
          ? <Buddy kind={buddy.buddy} mood="cheer" hat={moment.kind === 'level' ? hatFor(moment.level.index) : 1} size={150} />
          : badge && <span className="badge-medal big is-earned"><Icon name={badge.icon} size={44} /></span>}
        {moment.kind === 'level' ? (
          <>
            <h2 id="moment-title">You're a {moment.level.title}</h2>
            <p className="muted">Level {moment.level.index + 1}. {moment.level.nextTitle ? `Next up: ${moment.level.nextTitle}.` : 'The top of the kitchen.'}</p>
          </>
        ) : badge && (
          <>
            <h2 id="moment-title"><Icon name={badge.icon} size={26} /> {badge.title}</h2>
            <p className="muted">{badge.hint}.</p>
          </>
        )}
        <button type="button" className="primary" autoFocus onClick={onClose}>Nice!</button>
      </div>
    </div>
  )
}

/** The trophy shelf in Kitchen: every level and every badge, earned or not. */
export function Trophies({ summary, buddy, onBuddy }: {
  summary: KitchenSummary | null
  buddy: BuddyChoice | null
  onBuddy: (b: BuddyChoice) => void
}) {
  const [picking, setPicking] = useState(false)
  if (!summary) return <p className="muted">Loading…</p>
  if (picking || !buddy) return <BuddyPicker current={buddy} onCancel={buddy ? () => setPicking(false) : undefined} onChosen={(b) => { setPicking(false); onBuddy(b) }} />

  const earned = new Set(summary.badges.filter((b) => b.earned).map((b) => b.key))
  const f = summary.facts
  return (
    <>
      <section className="card">
        <div className="filter-row">
          <h2 style={{ margin: 0 }}>{buddyName(buddy)}'s kitchen</h2>
          <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setPicking(true)}>Switch buddy</button>
        </div>
        <div className="stats">
          <div><b>{summary.xp}</b><span>XP</span></div>
          <div><b>{f.cooks}</b><span>cooks</span></div>
          <div><b>{f.dishes}</b><span>dishes</span></div>
          <div><b>{summary.streak.best}</b><span>best streak</span></div>
        </div>
        <ol className="ladder">
          {LEVELS.map((l, i) => (
            <li key={l.title} className={i === summary.level.index ? 'is-here' : i < summary.level.index ? 'is-done' : ''}>
              <Buddy kind={buddy.buddy} mood={i === summary.level.index ? 'cheer' : 'happy'} hat={hatFor(i)} size={48} />
              <span><b>{l.title}</b><span className="muted small">{l.min} XP</span></span>
            </li>
          ))}
        </ol>
      </section>

      <section className="card">
        <h2>Badges <span className="muted small">{earned.size} of {BADGES.length}</span></h2>
        <ul className="badges">
          {BADGES.map((b) => (
            <li key={b.key} className={earned.has(b.key) ? 'is-earned' : ''}>
              <span className={earned.has(b.key) ? 'badge-medal is-earned' : 'badge-medal'}><Icon name={b.icon} size={22} /></span>
              <b>{b.title}</b>
              <span className="muted small">{b.hint}</span>
            </li>
          ))}
        </ul>
        <p className="muted small">
          {XP.save} XP for each recipe you save and {XP.cook} for each cook. Bonus: +{XP.newDish} for a new dish,
          +{XP.onPlan} for cooking on the day you planned it, +{XP.rescued} for rescuing a recipe that waited a week.
        </p>
      </section>
    </>
  )
}
